import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { resolveLocaleSwitchTarget } from "@/lib/seo/locale-navigation";
import { resolveSeoLocaleFromPathname } from "@/hooks/useSeoRouteLocale";

const LANGS = [
  { code: "fr" as const, short: "FR", nativeName: "Français" },
  { code: "en" as const, short: "EN", nativeName: "English" },
  { code: "ar" as const, short: "AR", nativeName: "العربية" },
];

const resolveActiveCode = (
  resolved: string | undefined,
  pathname: string
): "fr" | "en" | "ar" | null => {
  const fromPath = resolveSeoLocaleFromPathname(pathname);
  if (fromPath) return fromPath;
  const base = resolved?.split("-")[0] ?? "";
  if (base === "fr") return "fr";
  if (base === "en") return "en";
  if (base === "ar") return "ar";
  return null;
};

export interface LanguageSwitcherProps {
  className?: string;
  /** `compact` = navbar pill; `block` = full-width row (e.g. mobile sheet footer) */
  variant?: "compact" | "block";
  /**
   * Guest hero (transparent nav): false = light text on dark glass.
   * Logged-in bar, scrolled guest bar, or /search: true = readable on light surface.
   */
  useLightSurface?: boolean;
}

export const LanguageSwitcher = ({
  className,
  variant = "compact",
  useLightSurface,
}: LanguageSwitcherProps) => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const activeCode = resolveActiveCode(i18n.resolvedLanguage, location.pathname);
  const lightSurface = variant === "block" ? true : (useLightSurface ?? true);

  const handleLanguageChange = (langCode: string) => {
    if (langCode === activeCode) return;

    const target = resolveLocaleSwitchTarget(location.pathname, location.search, langCode);
    if (target) {
      void i18n.changeLanguage(langCode);
      navigate(target);
      return;
    }
    void i18n.changeLanguage(langCode);
  };

  return (
    <div
      role="group"
      aria-label={t("common.changeLanguage")}
      className={cn(
        variant === "compact" &&
          cn(
            "inline-flex items-center rounded-full p-0.5 backdrop-blur-sm",
            lightSurface
              ? "border border-border bg-muted/40"
              : "border border-white/25 bg-black/20"
          ),
        variant === "block" && "flex w-full items-stretch gap-2",
        className
      )}
    >
      {LANGS.map((lang) => {
        const isActive = activeCode === lang.code;
        return (
          <Button
            key={lang.code}
            type="button"
            variant="ghost"
            size="sm"
            title={lang.nativeName}
            aria-pressed={isActive}
            aria-label={`${t("common.changeLanguage")} — ${lang.nativeName}`}
            onClick={() => handleLanguageChange(lang.code)}
            className={cn(
              "min-h-[2.25rem] font-semibold tracking-wide transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400",
              variant === "compact" &&
                cn(
                  "h-9 rounded-full px-2.5 sm:px-3.5 text-xs sm:text-sm shrink-0",
                  isActive
                    ? lightSurface
                      ? "border border-green-night/15 bg-emerald-soft text-green-night shadow-sm hover:bg-emerald-soft hover:text-green-night"
                      : "bg-emerald-600 text-white shadow-sm hover:bg-emerald-600 hover:text-white"
                    : lightSurface
                      ? "text-text-secondary hover:bg-emerald-soft/60 hover:text-green-night"
                      : "text-white/90 hover:bg-white/10 hover:text-white"
                ),
              variant === "block" &&
                cn(
                  "flex-1 rounded-lg border text-sm",
                  isActive
                    ? "border-green-night bg-emerald-soft text-green-night hover:bg-emerald-soft hover:text-green-night"
                    : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                )
            )}
          >
            {lang.short}
          </Button>
        );
      })}
    </div>
  );
};

export default LanguageSwitcher;
